import {
  applyDurationChange,
  applyEndChange,
  applyTimeToDate,
  formatTimeMaskValue,
  getDurationMinutes,
  getTimePresets,
  parseTimeMaskValue,
  shiftRangeOnStartChange,
  TIME_PICKER_DEFAULT_MAX_DURATION_MINUTES,
  TimePickerPresetsConfig,
  TimePickerRangeValue,
} from "@/components/time-picker/time-picker.helper.ts";
import {
  TimePickerMode,
  TimePickerProps,
  TimePickerRangeProps,
  TimePickerSingleProps,
} from "@/components/time-picker/time-picker.types.ts";
import { Box, Button, FontAwesomeIcon, inputTheme, Stack, Text } from "@skailer-com/uikit";
import {
  autoUpdate,
  flip,
  FloatingContext,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { CSSProperties, useMemo, useRef, useState } from "react";
import { IMask, IMaskInput } from "react-imask";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Time } from "@/types/common.types.ts";

interface TimePresetsPopoverProps {
  context: FloatingContext;
  floatingRef: (node: HTMLElement | null) => void;
  floatingStyles: CSSProperties;
  floatingProps: Record<string, unknown>;
  value: Date;
  presetItems: Time[];
  title?: string;
  onSelect: (date: Date) => void;
  minDate?: Date;
}

function TimePresetsPopover({
  context,
  floatingRef,
  floatingStyles,
  floatingProps,
  value,
  presetItems,
  title,
  onSelect,
  minDate,
}: TimePresetsPopoverProps) {
  const hasCaptions = presetItems.some((preset) => Boolean(preset.caption));

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={false} initialFocus={-1} returnFocus={false}>
        <div
          ref={(node) => floatingRef(node)}
          style={floatingStyles}
          {...floatingProps}
          className={twMerge(
            "shadow-md bg-light rounded-sm overflow-y-auto min-w-52 p-2 grid gap-1",
            hasCaptions ? "grid-cols-1" : "grid-cols-3",
            "z-popover"
          )}
        >
          {title && <Text className="col-span-3 px-1 pb-1 text-secondary">{title}</Text>}
          {presetItems.map((preset) => {
            const isSelected = formatTimeMaskValue(value) === preset.id;
            const isDisabled = minDate ? preset.date.getTime() < minDate.getTime() : false;

            if (preset.caption) {
              return (
                <div
                  key={preset.id}
                  role="option"
                  tabIndex={isDisabled ? -1 : 0}
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  className={twMerge(
                    "w-full flex justify-between outline-0 gap-2 items-center text-default bg-transparent text-base px-2 shrink-0 rounded-xs py-1 min-h-10 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-dark/40",
                    isDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-dark/5 cursor-pointer",
                    isSelected ? "bg-dark/5 font-semibold" : undefined
                  )}
                  onClick={() => {
                    if (isDisabled) return;
                    onSelect(preset.date);
                  }}
                  onKeyDown={(event) => {
                    if (isDisabled || (event.key !== "Enter" && event.key !== " ")) return;
                    event.preventDefault();
                    onSelect(preset.date);
                  }}
                >
                  <Stack className={"min-w-0 flex-1"}>
                    <span className={"block max-w-full truncate text-ellipsis"}>
                      {preset.title}
                    </span>
                    <span className={"text-sm text-secondary font-normal"}>{preset.caption}</span>
                  </Stack>
                  {isSelected && <FontAwesomeIcon icon={"fa-light fa-check text-lg"} />}
                </div>
              );
            }

            return (
              <Button
                key={preset.id}
                variant={isSelected ? "primary" : "secondary"}
                size={"sm"}
                disabled={isDisabled}
                className={isDisabled ? "opacity-40 cursor-not-allowed" : undefined}
                leftIcon={isSelected ? <FontAwesomeIcon icon={"fa-light fa-check"} /> : undefined}
                onClick={() => {
                  if (isDisabled) return;
                  onSelect(preset.date);
                }}
              >
                {preset.title}
              </Button>
            );
          })}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}

function useTimeFieldPopover(
  value: Date,
  presets: TimePickerPresetsConfig | undefined,
  providedPresetItems?: Time[],
  showPresets = true
) {
  const [focused, setFocused] = useState(false);

  const { refs, floatingStyles, context } = useFloating<HTMLElement>({
    placement: "bottom-start",
    open: focused,
    onOpenChange: (isOpen) => {
      if (isOpen && focused) return;
      setFocused(isOpen);
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      shift({ padding: 10 }),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, { maxHeight: `${availableHeight}px` });
        },
        padding: 10,
      }),
    ],
  });

  const dismiss = useDismiss(context, {
    outsidePress: true,
    outsidePressEvent: "mousedown",
    ancestorScroll: true,
    escapeKey: true,
  });
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  const presetItems = useMemo(
    () => (showPresets ? (providedPresetItems ?? getTimePresets(value, presets)) : []),
    [value, presets, providedPresetItems, showPresets]
  );

  return {
    focused,
    setFocused,
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    presetItems,
  };
}

interface TimeFieldProps {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  value: Date | null;
  onChange: (date: Date) => void;
  presets?: TimePickerPresetsConfig;
  presetItems?: Time[];
  showPresets?: boolean;
  showMask?: boolean;
  withValueBackground?: boolean;
  selectTimePartOnFocus?: boolean;
  navigatePresetsWithArrows?: boolean;
  presetsTitle?: string;
  error?: string;
  className?: string;
}

function TimeField({
  label,
  required,
  disabled,
  readOnly,
  value,
  onChange,
  presets,
  presetItems: providedPresetItems,
  showPresets,
  showMask,
  withValueBackground,
  selectTimePartOnFocus,
  navigatePresetsWithArrows,
  presetsTitle,
  error,
  className,
}: TimeFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    focused,
    setFocused,
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    presetItems,
  } = useTimeFieldPopover(value ?? new Date(), presets, providedPresetItems, showPresets);

  const displayValue = value ? formatTimeMaskValue(value) : "";

  const handleAccept = (raw: string) => {
    const parsed = parseTimeMaskValue(raw);
    if (!parsed) return;

    onChange(applyTimeToDate(value ?? new Date(), parsed.hours, parsed.minutes));
  };

  const selectTimePart = (input: HTMLInputElement) => {
    if (!selectTimePartOnFocus) return;

    const caretPosition = input.selectionStart ?? 0;
    const isMinutes = caretPosition >= 3;
    input.setSelectionRange(isMinutes ? 3 : 0, isMinutes ? 5 : 2);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    if (event.key === "Tab" && selectTimePartOnFocus) {
      const selectionStart = input.selectionStart ?? 0;

      if (!event.shiftKey && selectionStart < 3) {
        event.preventDefault();
        input.setSelectionRange(3, 5);
        return;
      }

      if (event.shiftKey && selectionStart >= 3) {
        event.preventDefault();
        input.setSelectionRange(0, 2);
        return;
      }
    }

    if (
      navigatePresetsWithArrows &&
      providedPresetItems?.length &&
      (event.key === "ArrowDown" || event.key === "ArrowUp")
    ) {
      event.preventDefault();

      const currentId = value ? formatTimeMaskValue(value) : undefined;
      const currentIndex = providedPresetItems.findIndex((preset) => preset.id === currentId);
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        currentIndex === -1
          ? direction === 1
            ? 0
            : providedPresetItems.length - 1
          : Math.min(Math.max(currentIndex + direction, 0), providedPresetItems.length - 1);

      onChange(providedPresetItems[nextIndex].date);
    }
  };

  return (
    <Box className={className}>
      <Box
        ref={containerRef}
        className={twMerge(
          inputTheme.cover.base,
          disabled ? inputTheme.cover.disabled : undefined,
          readOnly ? inputTheme.cover.readonly : undefined,
          error ? inputTheme.cover.error : undefined,
          "h-14"
        )}
        onClick={() => {
          if (readOnly || disabled) return;
          if (focused) return;
          containerRef.current?.querySelector("input")?.focus();
        }}
      >
        <Stack
          direction="horizontal"
          ref={(node) => refs.setReference(node)}
          className={"w-full"}
          {...getReferenceProps()}
        >
          <label
            className={twMerge(
              inputTheme.label.base,
              disabled ? inputTheme.label.disabled : undefined,
              readOnly ? inputTheme.label.readonly : undefined,
              value || focused ? inputTheme.label.focused["md"] : inputTheme.label.usual["md"],
              "pointer-events-auto"
            )}
          >
            {label} {required && <span className={"text-danger"}>*</span>}
          </label>

          <IMaskInput
            className={twMerge(
              inputTheme.input.base,
              inputTheme.input.variants.size["md"],
              withValueBackground
                ? "field-sizing-content w-auto h-5 pt-0 pb-0 mt-6.5 rounded-sm bg-dark/5 px-1 text-center focus:bg-dark/7 focus:shadow-sm"
                : undefined,
              disabled ? inputTheme.input.disabled : undefined,
              readOnly ? inputTheme.input.readonly : undefined
            )}
            mask={"HH:MM"}
            blocks={{
              HH: { mask: IMask.MaskedRange, from: 0, to: 23, maxLength: 2 },
              MM: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2 },
            }}
            lazy={!showMask}
            value={displayValue}
            disabled={disabled}
            readOnly={readOnly}
            onFocus={(event) => {
              if (readOnly || disabled) return;
              setFocused(true);
              const input = event.currentTarget;
              requestAnimationFrame(() => selectTimePart(input));
            }}
            onClick={(event) => selectTimePart(event.currentTarget)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setTimeout(() => {
                const activeElement = document.activeElement;

                if (
                  refs.floating.current?.contains(activeElement) ||
                  containerRef.current?.contains(activeElement)
                ) {
                  return;
                }

                setFocused(false);
              }, 0);
            }}
            onAccept={(rawValue: string) => handleAccept(rawValue)}
          />
        </Stack>

        {focused && presetItems.length > 0 && (
          <TimePresetsPopover
            context={context}
            floatingRef={refs.setFloating}
            floatingStyles={floatingStyles}
            floatingProps={getFloatingProps()}
            value={value ?? new Date()}
            presetItems={presetItems}
            title={presetsTitle}
            onSelect={(date) => {
              onChange(date);
              setFocused(false);
            }}
          />
        )}
      </Box>
      {error && (
        <Box className={"pt-1"}>
          <Text className={"text-danger text-sm"}>{error}</Text>
        </Box>
      )}
    </Box>
  );
}

function TimePickerSingle({
  label,
  required,
  disabled,
  readOnly,
  value,
  onChange,
  error,
  presets,
  presetItems,
  showPresets,
  showMask,
  withValueBackground,
  selectTimePartOnFocus,
  navigatePresetsWithArrows,
  presetsTitle,
  className,
}: TimePickerSingleProps) {
  return (
    <TimeField
      label={label}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      value={value}
      onChange={onChange}
      presets={presets}
      presetItems={presetItems}
      showPresets={showPresets}
      showMask={showMask}
      withValueBackground={withValueBackground}
      selectTimePartOnFocus={selectTimePartOnFocus}
      navigatePresetsWithArrows={navigatePresetsWithArrows}
      presetsTitle={presetsTitle}
      error={error}
      className={className}
    />
  );
}

interface TimeSegmentProps {
  value: Date;
  onChange: (date: Date) => void;
  className?: string;
  presets?: TimePickerPresetsConfig;
  presetsTitle?: string;
  disabled?: boolean;
  readOnly?: boolean;
  minDate?: Date;
}

function TimeSegment({
  value,
  onChange,
  className,
  presets,
  presetsTitle,
  disabled,
  readOnly,
  minDate,
}: TimeSegmentProps) {
  const {
    focused,
    setFocused,
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    presetItems,
  } = useTimeFieldPopover(value, presets);

  const displayValue = formatTimeMaskValue(value);

  const handleAccept = (raw: string) => {
    const parsed = parseTimeMaskValue(raw);
    if (!parsed) return;

    onChange(applyTimeToDate(value, parsed.hours, parsed.minutes));
  };

  return (
    <>
      <IMaskInput
        className={twMerge(
          "bg-transparent border-none outline-0 font-semibold text-dark text-base text-center field-sizing-content px-2 rounded-sm bg-dark/5 focus:bg-dark/7 focus:shadow-sm",
          className
        )}
        mask={"HH:MM"}
        blocks={{
          HH: { mask: IMask.MaskedRange, from: 0, to: 23, maxLength: 2 },
          MM: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2 },
        }}
        lazy={false}
        value={displayValue}
        disabled={disabled}
        readOnly={readOnly}
        inputRef={(node) => refs.setReference(node)}
        {...getReferenceProps()}
        onFocus={() => {
          if (readOnly || disabled) return;
          setFocused(true);
        }}
        onBlur={() => {
          setTimeout(() => {
            const activeElement = document.activeElement;

            if (refs.floating.current?.contains(activeElement)) {
              return;
            }

            setFocused(false);
          }, 0);
        }}
        onAccept={(rawValue: string) => handleAccept(rawValue)}
      />

      {focused && presetItems.length > 0 && (
        <TimePresetsPopover
          context={context}
          floatingRef={refs.setFloating}
          floatingStyles={floatingStyles}
          floatingProps={getFloatingProps()}
          value={value}
          presetItems={presetItems}
          title={presetsTitle}
          minDate={minDate}
          onSelect={(date) => {
            onChange(date);
            setFocused(false);
          }}
        />
      )}
    </>
  );
}

function TimePickerRange({
  label,
  required,
  disabled,
  readOnly,
  value,
  onChange,
  error,
  showDuration = true,
  maxDurationMinutes = TIME_PICKER_DEFAULT_MAX_DURATION_MINUTES,
  presets,
  startPresetsTitle,
  endPresetsTitle,
  className,
}: TimePickerRangeProps) {
  const { t } = useTranslation();

  const [endFieldError, setEndFieldError] = useState<string | undefined>(undefined);
  const [durationFieldError, setDurationFieldError] = useState<string | undefined>(undefined);
  const [durationDraft, setDurationDraft] = useState<string>(() =>
    String(getDurationMinutes(value))
  );
  const [durationRangeSnapshot, setDurationRangeSnapshot] = useState<TimePickerRangeValue>(value);

  if (durationRangeSnapshot !== value) {
    setDurationRangeSnapshot(value);
    setDurationDraft(String(getDurationMinutes(value)));
    setDurationFieldError(undefined);
  }

  const handleStartChange = (newStart: Date) => {
    setEndFieldError(undefined);
    onChange(shiftRangeOnStartChange(value, newStart));
  };

  const handleEndChange = (newEnd: Date) => {
    const result = applyEndChange(value, newEnd);

    if (result.error) {
      setEndFieldError(t("errors:end_time_should_be_greater_start_time"));
      return;
    }

    setEndFieldError(undefined);
    onChange(result.range);
  };

  const commitDuration = () => {
    const durationMinutes = parseInt(durationDraft, 10);
    const result = applyDurationChange(value, durationMinutes, maxDurationMinutes);

    if (result.error === "max_exceeded") {
      setDurationFieldError(
        t("errors:duration_exceeds_max_hours", { hours: maxDurationMinutes / 60 })
      );
      setDurationDraft(String(getDurationMinutes(value)));
      return;
    }

    if (result.error === "invalid") {
      setDurationFieldError(t("errors:field_required"));
      setDurationDraft(String(getDurationMinutes(value)));
      return;
    }

    setDurationFieldError(undefined);
    onChange(result.range);
  };

  const displayedError = error ?? endFieldError;

  return (
    <Box className={className}>
      <Box
        className={twMerge(
          inputTheme.cover.base,
          inputTheme.cover.variants.size["md"],
          disabled ? inputTheme.cover.disabled : undefined,
          readOnly ? inputTheme.cover.readonly : undefined,
          displayedError ? inputTheme.cover.error : undefined
        )}
      >
        <label className={twMerge(inputTheme.label.base, inputTheme.label.focused["md"])}>
          {label ?? t("common:time")} {required && <span className={"text-danger"}>*</span>}
        </label>

        <Stack
          direction="horizontal"
          items="center"
          justify="between"
          className={twMerge(
            inputTheme.input.base,
            inputTheme.input.variants.size["md"],
            disabled ? inputTheme.input.disabled : undefined,
            readOnly ? inputTheme.input.readonly : undefined
          )}
        >
          <Stack direction="horizontal" items="center" gap="xs" className={"mt-px tabular-nums"}>
            <TimeSegment
              value={value.start}
              onChange={handleStartChange}
              presets={presets}
              presetsTitle={startPresetsTitle}
              disabled={disabled}
              readOnly={readOnly}
            />
            <Text className={"text-dark/40"}>—</Text>
            <TimeSegment
              value={value.end}
              onChange={handleEndChange}
              presets={presets}
              presetsTitle={endPresetsTitle}
              disabled={disabled}
              readOnly={readOnly}
              minDate={value.start}
            />
          </Stack>

          {showDuration && (
            <Stack
              direction="horizontal"
              items="center"
              gap="xs"
              className={twMerge(
                "py-1",
                durationFieldError ? "outline outline-1 outline-danger" : undefined
              )}
            >
              <input
                className={
                  "bg-transparent border-none outline-0 font-mono font-semibold text-dark text-sm text-center"
                }
                style={{ width: `${Math.max(durationDraft.length, 1) + 0.5}ch` }}
                inputMode={"numeric"}
                disabled={disabled}
                readOnly={readOnly}
                value={durationDraft}
                onChange={(event) => setDurationDraft(event.target.value.replace(/\D/g, ""))}
                onBlur={commitDuration}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.currentTarget.blur();
                  }
                }}
              />
              <Text className={"text-sm text-dark/60"}>{t("common:minutes_unit")}</Text>
            </Stack>
          )}
        </Stack>
      </Box>

      {displayedError && (
        <Box className={"pt-1"}>
          <Text className={"text-danger text-sm"}>{displayedError}</Text>
        </Box>
      )}
      {durationFieldError && (
        <Box className={"pt-1"}>
          <Text className={"text-danger text-sm"}>{durationFieldError}</Text>
        </Box>
      )}
    </Box>
  );
}

export function TimePicker(props: TimePickerProps) {
  if (props.mode === TimePickerMode.range) {
    return <TimePickerRange {...props} />;
  }

  return <TimePickerSingle {...props} />;
}
